/**
 * AgentSessionList Component
 * 
 * Displays a list of conversation sessions with the ability to:
 * - Select a session to continue the conversation
 * - Archive/unarchive sessions
 * - Delete sessions
 * - Filter active vs archived sessions
 * - Start new conversations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { agentApi } from '../../services/api';
import type { ConversationSessionListItem } from '../../types/agent';
import { getTextDirection, muiBidiTextSx } from '../chat/textDirection';
import { formatDistanceToNow } from 'date-fns';

const PLACEHOLDER_TITLES = new Set(['new conversation', 'untitled chat', 'untitled conversation']);

const cleanSessionPreview = (value?: string | null) => {
  if (!value) return '';
  return value
    .replace(/[#*_`[\]]/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const isPlaceholderTitle = (value?: string | null) => {
  const normalized = cleanSessionPreview(value).toLowerCase();
  return !normalized || PLACEHOLDER_TITLES.has(normalized);
};

const getSessionDisplayTitle = (session: ConversationSessionListItem) => {
  const title = cleanSessionPreview(session.title);
  if (!isPlaceholderTitle(title)) return title;

  const preview = cleanSessionPreview(
    session.first_message_preview || session.last_message_preview?.content
  );
  return preview || title || 'New Conversation';
};

interface AgentSessionListProps {
  agentId: number;
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string | null) => void;
  onNewConversation: () => void;
}

const AgentSessionList: React.FC<AgentSessionListProps> = ({
  agentId,
  currentSessionId,
  onSessionSelect,
  onNewConversation,
}) => {
  const [sessions, setSessions] = useState<ConversationSessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'active' | 'archived'>('active');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  // Load sessions
  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agentApi.getSessions(
        agentId,
        filter === 'active',
        1,
        50 // Load first 50 sessions
      );
      setSessions(response.results);
    } catch (err: any) {
      console.error('Failed to load sessions:', err);
      setError(err.response?.data?.detail || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, [agentId, filter]);

  // Handle archive/unarchive
  const handleToggleArchive = async (sessionId: string, isActive: boolean) => {
    try {
      if (isActive) {
        await agentApi.archiveSession(sessionId);
      } else {
        await agentApi.unarchiveSession(sessionId);
      }
      loadSessions();
    } catch (err: any) {
      console.error('Failed to toggle archive:', err);
      setError(err.response?.data?.detail || 'Failed to update session');
    }
  };

  // Handle delete
  const handleDeleteClick = (sessionId: string) => {
    setSessionToDelete(sessionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;

    try {
      await agentApi.deleteSession(sessionToDelete);
      
      // If deleted current session, clear selection
      if (sessionToDelete === currentSessionId) {
        onSessionSelect(null);
      }
      
      loadSessions();
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete session:', err);
      setError(err.response?.data?.detail || 'Failed to delete conversation');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'No messages';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header with New Conversation button */}
      <Box sx={{ p: 2.5, pt: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewConversation}
          sx={{ 
            mb: 2, 
            py: 1.2,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 600,
            boxShadow: 2,
          }}
        >
          New Conversation
        </Button>

        {/* Filter toggle */}
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => value && setFilter(value)}
          fullWidth
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
          }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Session list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ChatIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              {filter === 'active' ? 'No active conversations' : 'No archived conversations'}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
              {filter === 'active' ? 'Start a new conversation to begin' : 'Your archived chats will appear here'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ bgcolor: 'background.paper' }}>
            {sessions.map((session) => {
              const sessionTitle = getSessionDisplayTitle(session);

              return (
              <ListItem
                key={session.id}
                disablePadding
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleArchive(session.id, session.is_active);
                        }}
                        sx={{ 
                          opacity: 0.7,
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        {session.is_active ? <ArchiveIcon fontSize="small" /> : <UnarchiveIcon fontSize="small" />}
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(session.id);
                        }}
                        sx={{ 
                          opacity: 0.7,
                          '&:hover': { opacity: 1, color: 'error.main' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                  </Box>
                }
              >
                <ListItemButton
                  selected={currentSessionId === session.id}
                  onClick={() => onSessionSelect(session.id)}
                  sx={{ 
                    pr: 14,
                    py: 1.5,
                    '&.Mui-selected': {
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.06)',
                      borderLeft: 3,
                      borderColor: 'primary.main',
                      '&:hover': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : 'rgba(25, 118, 210, 0.08)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Typography
                          dir={getTextDirection(sessionTitle)}
                          variant="body2"
                          sx={{ 
                            ...muiBidiTextSx,
                            flex: 1,
                            fontWeight: currentSessionId === session.id ? 600 : 500,
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {sessionTitle}
                        </Typography>
                        <Chip
                          label={session.message_count}
                          size="small"
                          color={currentSessionId === session.id ? 'primary' : 'default'}
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography 
                        variant="caption" 
                        color="text.disabled"
                        sx={{ 
                          fontSize: '0.7rem',
                          display: 'block',
                          mt: 0.5,
                        }}
                      >
                        {formatTimestamp(session.last_message_at)}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Conversation?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentSessionList;
